import { compact, flatten, get, sortBy } from 'lodash-es';
import { PAID_CODE, FREE_CODE } from 'site-modules/shared/components/delivery/constants';
import { LEASE_PAYMENT, LOAN_PAYMENT } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { isCpo } from 'site-modules/shared/utils/inventory-utils/is-cpo';
import { getVdpUrl } from 'site-modules/shared/utils/inventory-utils/get-vdp-url';
import { isTexas } from 'site-modules/shared/utils/inventory-utils/is-texas';
import { getDifferenceInDays } from 'site-modules/shared/utils/date-utils';
import { getCabType, getNonPickupVehicleStyle } from 'site-modules/shared/utils/get-cab-type';
import {
  isAMSI,
  isCarMax,
  isDeliveryEligible,
  isDriveway,
  isFiveStarDealer as getIsFiveStarDealer,
  isInstockOnline,
  isPartnerDealer,
  isTransferDealership,
  isCarZingPartner,
  isPrivateParty,
} from 'site-modules/shared/utils/dealer-details-utils';
import { isLeasePaymentsEligible } from 'client/site-modules/shared/utils/inventory-utils/is-lease-payments-eligible';
import { isNew as isNewType } from 'site-modules/shared/utils/inventory-utils/is-new';
import { getEngineTypeValue } from 'site-modules/shared/utils/inventory/get-engine-type-value';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { getPlural } from 'client/utils/plural';
import {
  getRecurrentRangeValue,
  getRecurrentScoreValue,
} from 'site-modules/shared/components/usurp-inventory-card/usurp-inventory-card-helper';
import {
  BODY_TYPES_SRP_EXPANDED,
  IN_STOCK_ONLINE,
} from 'site-modules/shared/components/inventory/constants/usurp-inventory-card';
import React, { Fragment } from 'react';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { toShortFormattedAddress } from 'site-modules/shared/utils/address-format';
import { getEngineAttrIcon } from 'site-modules/shared/components/inventory/utils/get-engine-attr-icon';
import { getEngineAttr } from 'site-modules/shared/components/inventory/utils/get-engine-attr';
import { DEFAULT_PRICE } from 'site-modules/shared/components/deal/deal-constants';
import { isUsedDealCheckAvailable } from 'site-modules/shared/utils/car-buying/is-used-deal-check-available';
import { buildVinPreviewSrpLink } from 'site-modules/shared/utils/srp-link-constructor';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { showTrimName } from 'site-modules/shared/utils/show-trim-name';

const BODY_TYPES_ENHANCED = ['Hatchback', 'Coupe', 'Convertible'];

export const INVENTORY_BADGE_LABELS = {
  PRICE_DROP: 'Price Drop',
  NEW_LISTING: 'New Listing',
  ARRIVING_SOON: 'Arriving Soon',
  EV_REBATE: 'EV Rebates',
  SALE_BY_OWNER: 'For Sale By Owner',
};

function getCarmaxTransferText({ isNational, vehicle, radius, visitorStateCode }) {
  if (isNational) {
    return 'Transfer available';
  }

  const { distance, address } = vehicle?.dealerInfo || {};
  const { city, stateCode: vinStateCode } = address || {};

  if (distance > radius) {
    if (vinStateCode !== visitorStateCode) {
      return `Transfer available from ${vinStateCode} to ${visitorStateCode}`;
    }
    if (city && vinStateCode) {
      return `Transfer available from ${city}, ${vinStateCode}`;
    }
  }

  return '';
}

function getNonCarmaxDeliveryText({ isNational, vehicle, deliveryCode }) {
  if (isNational) {
    return 'Delivery available';
  }

  const parentDealershipName = vehicle?.dealerInfo?.displayInfo?.parentDealershipName ?? '';
  const isTransfer = isTransferDealership(parentDealershipName);

  if (deliveryCode === PAID_CODE) {
    return isTransfer ? 'Delivery available' : 'Home delivery';
  }

  if (deliveryCode === FREE_CODE) {
    return isTransfer ? 'Free delivery available' : 'Free home delivery';
  }

  return '';
}

export function getDeliveryText({ isNational, vehicle, isCarmaxDealer, radius, visitorStateCode }) {
  const deliveryCode = vehicle?.computedDisplayInfo?.deliveryOptions?.deliveryCode;

  if (!deliveryCode) {
    return '';
  }

  if (isCarmaxDealer) {
    return getCarmaxTransferText({ isNational, vehicle, radius, visitorStateCode });
  }

  return getNonCarmaxDeliveryText({ isNational, vehicle, deliveryCode });
}

/** @deprecated TODO: remove after pricing migration is complete (use isDisplayBelowMarketCopy instead) */
export function getPaymentDetails({ prices, usePaymentType, selectedPaymentType, paymentType }) {
  const paymentTypeValue = usePaymentType ? paymentType : selectedPaymentType;
  const loanPayment = get(prices, 'loan.payment', 0);
  const leasePayment = get(prices, 'estimateLeasePromise.monthlyPayment', 0);

  let paymentTitle = null;
  let paymentValue = null;

  if (loanPayment && paymentTypeValue === LOAN_PAYMENT) {
    paymentTitle = 'loan';
    paymentValue = formatPriceString(loanPayment);
  } else if (leasePayment && paymentTypeValue === LEASE_PAYMENT) {
    paymentTitle = 'lease';
    paymentValue = formatPriceString(leasePayment);
  }

  return {
    paymentTitle,
    paymentValue,
  };
}

/** @deprecated TODO: remove after pricing migration is complete (use isDisplayBelowMarketCopy instead) */
export function getDisplayBelowMarketCopy({ paymentValue, isNew, dealerStateCode, savings }) {
  return !paymentValue && !isNew && !isTexas(dealerStateCode) && savings >= 200;
}

export function isDisplayBelowMarketCopy({ hasMonthlyPayment, discountPriceIsCompliant, savings }) {
  return !hasMonthlyPayment && !!discountPriceIsCompliant && !!savings;
}

export function getTrimForDisplaying(vehicle) {
  const { make, trim = '' } = get(vehicle, 'vehicleInfo.styleInfo', {});
  return showTrimName(make, trim) ? trim.trim() : '';
}

export function getVehicleName(vehicle, isEnhanced = false, returnVehicleNameLines = false) {
  const { vehicleInfo, type } = vehicle;
  const { make, model, year, specialIdentifier: specialIdentifierRaw = '' } = get(vehicleInfo, 'styleInfo', {});
  const specialIdentifier = /^w\/Prod\. End/.test(specialIdentifierRaw) ? '' : specialIdentifierRaw;
  const styleInfo = get(vehicleInfo, 'styleInfo');
  const bodyType = get(styleInfo, 'bodyType');
  const isCpoType = isCpo(type);
  const cabType = getCabType(styleInfo);
  const nonPickupVehicleStyle = getNonPickupVehicleStyle(styleInfo);
  const trim = getTrimForDisplaying(vehicle);

  let vehicleName = `${isCpoType ? 'Certified' : ''} ${year} ${make} ${model} ${trim}`.trim();
  const firstLine = `${isCpoType ? 'Certified' : ''} ${year} ${make} ${model}`.trim();
  let secondLine = `${trim}`.trim();

  if (isEnhanced) {
    const engineTypeValue = getEngineTypeValue({ model, trim, engineType: get(vehicleInfo, 'partsInfo.engineType') });
    vehicleName = [vehicleName, engineTypeValue].join(' ');
    secondLine = [secondLine, engineTypeValue].join(' ');
  }

  vehicleName = `${vehicleName} ${cabType}`.trim();
  secondLine = `${secondLine} ${cabType}`.trim();

  if (returnVehicleNameLines) {
    return [firstLine, compact([secondLine, nonPickupVehicleStyle, specialIdentifier]).join(' ')];
  }

  if (isEnhanced && BODY_TYPES_ENHANCED.includes(bodyType) && !model.includes(bodyType) && !trim.includes(bodyType)) {
    vehicleName = `${vehicleName} ${bodyType}`;
  }

  return vehicleName;
}

export function getVdpLink({ vehicle, isNational, paymentType, radius, buyonline, deliveryFee }) {
  const skipLeasePaymentType = paymentType === LOAN_PAYMENT || !isLeasePaymentsEligible(vehicle);
  return getVdpUrl({ inventory: vehicle, isNational, skipLeasePaymentType, radius, buyonline, deliveryFee });
}

const isEvRebateBadgeApplicable = vinInfo => !!vinInfo.incentives?.isEvEligible;

export function getInventoryBadgeText(vinInfo, showInTransit) {
  const { firstPublishedDate, prices, inTransit } = vinInfo;
  const { advertisedPriceDelta, advertisedPriceDeltaTimestamp, historicalPricesIsCompliant } = prices;

  if (isPrivateParty(vinInfo)) {
    return INVENTORY_BADGE_LABELS.SALE_BY_OWNER;
  }

  if (isEvRebateBadgeApplicable(vinInfo)) {
    return INVENTORY_BADGE_LABELS.EV_REBATE;
  }

  if (showInTransit && inTransit && isPartnerDealer(vinInfo)) {
    return INVENTORY_BADGE_LABELS.ARRIVING_SOON;
  }

  if (getDifferenceInDays(new Date(firstPublishedDate)) < 7) {
    return INVENTORY_BADGE_LABELS.NEW_LISTING;
  } else if (
    historicalPricesIsCompliant &&
    advertisedPriceDelta < 0 &&
    getDifferenceInDays(new Date(advertisedPriceDeltaTimestamp)) < 7
  ) {
    return INVENTORY_BADGE_LABELS.PRICE_DROP;
  }

  return null;
}

export function transformDerivedFeatures(vehicle) {
  return sortBy(get(vehicle, 'derivedGenericFeatures', []), ['displayPriority']).map(({ optionText }) => ({
    name: optionText,
  }));
}

export function getKeyPoints({
  vehicle,
  visitorLocation,
  radius,
  isNational,
  showNationalDelivery,
  isSrp,
  showRecurrentData,
  isPrivatePartyListing,
}) {
  const { vehicleInfo, dealerInfo, type } = vehicle;
  const isNewVinType = isNewType(type);
  const mileage = get(vehicleInfo, 'mileage', 0);
  const distance = Math.round(get(dealerInfo, 'distance', 0));

  const dealerAddress = get(dealerInfo, 'address');

  const parentDealershipName = get(vehicle, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
  const isDrivewayDealer = isDriveway(parentDealershipName);
  const isCarmaxDealer = isCarMax(parentDealershipName);
  const zipCode = visitorLocation?.zipCode;
  const visitorStateCode = visitorLocation?.stateCode;
  const deliveryText = getDeliveryText(
    isSrp ? { vehicle, isNational, isCarmaxDealer, radius, visitorStateCode } : { vehicle, isNational }
  );
  const isFiveStarDealer = getIsFiveStarDealer(vehicle);
  const isDeliveryAvailable = isDeliveryEligible(vehicle, zipCode);
  let dealerLocationLabel;
  const isInStockOnline =
    (!isNewVinType || isDrivewayDealer) && isInstockOnline({ distance, radius, isDeliveryAvailable });
  if (isPrivatePartyListing) {
    const sellerInfo = get(vehicle, 'sellerInfo', {});
    const sellerAddress = get(sellerInfo, 'address', {});
    const city = get(sellerAddress, 'city', '');
    const stateCode = get(sellerAddress, 'stateCode', '');

    dealerLocationLabel = `Private Seller - ${city}, ${stateCode} (${numberWithCommas(distance)} mi away)`;
  } else if (showNationalDelivery) {
    dealerLocationLabel = isDeliveryAvailable ? IN_STOCK_ONLINE : toShortFormattedAddress(dealerAddress);
  } else {
    dealerLocationLabel = isInStockOnline ? IN_STOCK_ONLINE : `${numberWithCommas(distance)} mi away`;
  }
  const bodyType = get(vehicleInfo, 'styleInfo.bodyType');

  const isElectric = get(vehicle, 'vehicleInfo.partsInfo.engineType') === 'electric';
  const isPluginHybrid = get(vehicle, 'vehicleInfo.partsInfo.engineType') === 'plug-in hybrid';
  const electricityRange = get(vehicleInfo, 'partsInfo.electricityRange', '');
  const showElectricityRange = isElectric && !showRecurrentData;
  const engineAttrIcon = getEngineAttrIcon({ vehicleInfo, showElectricityRange });
  const engineAttr = getEngineAttr({
    vehicleInfo,
    showElectricityRange,
  });
  const recurrentRangeValue = getRecurrentRangeValue(vehicle);
  const recurrentScoreValue = getRecurrentScoreValue(vehicle);

  const specsKeypoints = [
    isPluginHybrid &&
      electricityRange && {
        icon: 'icon-low-miles',
        iconTitle: 'Electric range',
        content: `${electricityRange} mi est. electric range`,
      },
    BODY_TYPES_SRP_EXPANDED.includes(bodyType) &&
      !showRecurrentData &&
      engineAttr && {
        icon: engineAttrIcon,
        iconTitle: 'Engine attribute',
        content: engineAttr,
      },
    showRecurrentData && {
      icon: 'icon-low-miles',
      iconTitle: 'Current Est. Range',
      content: recurrentRangeValue,
    },
    showRecurrentData && {
      icon: 'icon-battery_charging_full2',
      iconTitle: 'Battery Range Score',
      content: recurrentScoreValue,
    },
  ];

  return compact([
    !isNewVinType && {
      icon: 'icon-meter',
      iconTitle: 'Car Mileage',
      content: mileage ? `${numberWithCommas(mileage)} ${getPlural('mile', mileage)}` : 'Not provided',
    },
    ...specsKeypoints,
    (!isNational || showNationalDelivery) && {
      icon: 'icon-location',
      iconTitle: 'Dealer Location',
      content: <span className="text-cool-gray-40">{dealerLocationLabel}</span>,
    },
    !!deliveryText && {
      icon: 'icon-transit',
      iconTitle: 'Shipping',
      content: <Fragment>{deliveryText}*</Fragment>,
      showFiveStar: isFiveStarDealer,
    },
    !deliveryText &&
      isFiveStarDealer && {
        icon: 'icon-5star text-blue-50',
        content: <span className="size-12 fw-medium text-blue-50">Five Star Dealer</span>,
      },
  ]);
}

export function getSimilarCardsKeyPoints({
  vehicle,
  visitorLocation,
  radius,
  isNational,
  showNationalDelivery,
  isSrp,
}) {
  const { vehicleInfo, dealerInfo, type } = vehicle;
  const isNewVinType = isNewType(type);
  const mileage = get(vehicleInfo, 'mileage', 0);
  const distance = Math.round(get(dealerInfo, 'distance', 0));

  const dealerAddress = get(dealerInfo, 'address');

  const parentDealershipName = get(vehicle, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
  const isDrivewayDealer = isDriveway(parentDealershipName);
  const isCarmaxDealer = isCarMax(parentDealershipName);
  const zipCode = visitorLocation?.zipCode;
  const visitorStateCode = visitorLocation?.stateCode;
  const deliveryText = getDeliveryText(
    isSrp ? { vehicle, isNational, isCarmaxDealer, radius, visitorStateCode } : { vehicle, isNational }
  );
  const isFiveStarDealer = getIsFiveStarDealer(vehicle);
  const isDeliveryAvailable = isDeliveryEligible(vehicle, zipCode);
  let dealerLocationLabel;
  const isInStockOnline =
    (!isNewVinType || isDrivewayDealer) && isInstockOnline({ distance, radius, isDeliveryAvailable });
  if (showNationalDelivery) {
    dealerLocationLabel = isDeliveryAvailable ? IN_STOCK_ONLINE : toShortFormattedAddress(dealerAddress);
  } else {
    dealerLocationLabel = isInStockOnline
      ? IN_STOCK_ONLINE
      : `${numberWithCommas(distance)} ${getPlural('mile', distance)} away`;
  }

  return compact([
    !isNewVinType && {
      icon: 'icon-meter',
      iconTitle: 'Car Mileage',
      content: mileage ? `${numberWithCommas(mileage)} ${getPlural('mile', mileage)}` : 'Not provided',
    },
    (!isNational || showNationalDelivery) && {
      icon: 'icon-location',
      iconTitle: 'Dealer Location',
      content: dealerLocationLabel,
    },
    isFiveStarDealer && {
      icon: 'icon-5star',
      content: 'Five Star Dealer ',
    },
    deliveryText && {
      icon: 'icon-transit',
      iconTitle: 'Shipping',
      content: <Fragment>{deliveryText}*</Fragment>,
    },
  ]);
}

export function getLlmCardKeyPoints({ vehicle }) {
  const { vehicleInfo, vin, type } = vehicle;
  const isNewVinType = isNewType(type);
  const mileage = get(vehicleInfo, 'mileage', 0);

  return compact([
    !isNewVinType && {
      icon: 'icon-meter',
      iconTitle: 'Car Mileage',
      content: mileage ? `${numberWithCommas(mileage)} ${getPlural('mile', mileage)}` : 'Not provided',
    },
    vin && {
      icon: 'icon-price-tag3',
      iconTitle: 'Vin',
      content: vin,
    },
  ]);
}

export function getIsLeaseCardWithLeasePayment({ selectedPaymentType, prices }) {
  const isLeaseCard = selectedPaymentType === LEASE_PAYMENT;
  const leasePayment = get(prices, 'estimateLeasePromise.monthlyPayment', 0);
  return Boolean(isLeaseCard && leasePayment);
}

export function getIsLoanCardWithLoanPayment({ selectedPaymentType, prices }) {
  const isLoanCard = selectedPaymentType === LOAN_PAYMENT;
  const loanPayment = get(prices, 'loan.payment', 0);
  return Boolean(isLoanCard && loanPayment);
}

export function getVehicleNameAriaLabel({ isSimilarCards, vehicle, isEnhancedName, prices }) {
  const vehicleName = flatten([getVehicleName(vehicle, isEnhancedName, !isSimilarCards)]).join(' ');

  const vehiclePrice = get(prices, 'advertisedPrice', 0);
  return `${vehiclePrice ? formatPriceString(vehiclePrice) : DEFAULT_PRICE} ${vehicleName}`;
}

export function getTCPAParentDealershipName({ parentDealershipName, partnerCode }) {
  const isCarMaxDealer = isCarMax(parentDealershipName);
  const isAMSIDealer = isAMSI(parentDealershipName);
  const isCarZingDealer = isCarZingPartner(partnerCode);

  if (isCarMaxDealer || isAMSIDealer) {
    return parentDealershipName;
  }

  return isCarZingDealer ? partnerCode : null;
}

export function isUsedCTAAvailable({ vehicle }) {
  const isPartner = isPartnerDealer(vehicle);
  return isUsedDealCheckAvailable(vehicle) || isPartner;
}

export function checkDealerSupportsCTA({ vehicle, disableDrivewayNewCarLeadForms }) {
  const isDrivewayDealer = isDriveway(vehicle.dealerInfo?.displayInfo?.parentDealershipName);
  const isNew = isNewType(vehicle.type);

  return !(isDrivewayDealer && disableDrivewayNewCarLeadForms && isNew);
}

export function isCTAAvailable({ vehicle, disableDrivewayNewCarLeadForms, isPrivatePartyListing }) {
  const isNewVinType = isNewType(vehicle.type);

  return (
    !isPrivatePartyListing &&
    checkDealerSupportsCTA({ vehicle, disableDrivewayNewCarLeadForms }) &&
    (isNewVinType || isUsedCTAAvailable({ vehicle }))
  );
}

export function getInventoryCardVdpLink({ vehicle, paymentType, radius, isNational, isVinPreviewLink, isLlmCard }) {
  switch (true) {
    case isVinPreviewLink: {
      return buildVinPreviewSrpLink(vehicle.vin, {
        make: makeNiceName(vehicle?.vehicleInfo?.styleInfo?.make),
        model: makeNiceName(vehicle?.vehicleInfo?.styleInfo?.model),
        year: vehicle?.vehicleInfo?.styleInfo?.year,
      });
    }
    case isLlmCard: {
      return getVdpUrl({ inventory: vehicle, isNational, radius });
    }
    default: {
      return getVdpLink({ vehicle, isNational, paymentType, radius });
    }
  }
}
